import React from "react";
import { Building2 } from "lucide-react";
import { useAuth } from "../../lib/auth-context";
import { roleLabel } from "../../lib/role-labels";

import { useTranslation } from "react-i18next";

/**
 * Lets someone who belongs to several clubs move this browser session between
 * them. Switching reloads the app in the new club: the role, menus and every
 * list depend on the club, so nothing from the previous one may linger.
 * Hidden for single-club users, while impersonating (read-only view) and
 * during break-glass access (the banner is the way out then).
 */
export function ClubSwitcher() {
  const { t } = useTranslation("common");
  const { user, switchClub } = useAuth();

  const memberships = (user?.memberships ?? []).filter((m) => m.status === "ACTIVE");
  if (!user || user.impersonator || user.accessGrant || memberships.length < 2) return null;

  const activeId = user.club ? String(user.club.id) : "";

  const onChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    await switchClub(Number(e.target.value));
    window.location.href = "/";
  };

  return (
    <label className="ml-auto flex min-w-0 shrink items-center gap-1.5 text-sm text-header-foreground/70">
      <Building2 className="h-4 w-4 shrink-0" aria-hidden />
      <span className="sr-only">{t("clubSwitcher.clubActif")}</span>
      <select
        value={activeId}
        onChange={onChange}
        className="max-w-[9rem] truncate rounded-md border border-border bg-background px-2 py-1 text-foreground sm:max-w-[16rem]"
      >
        {memberships.map((m) => (
          <option key={m.id} value={m.club.id}>
            {m.club.name} · {roleLabel(m.role)}
          </option>
        ))}
      </select>
    </label>
  );
}
